import Joi from 'joi'
import { useEffect, useState } from 'react'
import { MdCheckCircle } from 'react-icons/md'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useUiStore } from 'store'
import { GButton, GInput } from '../../../components/basic-blocks'
import { useStores } from '../../../hooks'
import { BsCircleFill } from 'react-icons/bs'
import { FiEye } from 'react-icons/fi'
import { HiOutlineEyeOff } from 'react-icons/hi'
import { ResetPasswordDTO } from '../../../services/user/types'
import { NewOnboarding } from '../../../components/new-onboarding'

const passwordSchema = Joi.object({
	password: Joi.string()
		.pattern(new RegExp('^(?=.*[A-Z])(?=.*\\d).{8,}$'))
		.required()
		.messages({
			'string.pattern.base':
				'Password must be at least 8 characters long and contain at least one special character.'
		})
})

const validatePassword = (password: string) => {
	const { error } = passwordSchema.validate({ password })
	return error ? error.details[0].message : null
}

const changePasswordSteps = {
	change_password: {
		title: 'Create your password',
		sub_title: 'This password will be used to log into your account.'
	},
	link_expired: {
		title: 'Link Expired',
		sub_title:
			'Your password reset link is no longer valid. Try requesting a new link to reset your password.'
	},
	not_found: {
		title: 'Invalid or Missing Link',
		sub_title:
			'The password reset link you used is invalid or could not be found. Please request a new link to continue.'
	}
}

export const ChangePassword = () => {
	const navigate = useNavigate()
	const [step, setStep] = useState<
		'link_expired' | 'not_found' | 'change_password'
	>('change_password')
	const [passwordRequest, setPasswordRequest] =
		useState<ResetPasswordDTO | null>(null)
	const [search] = useSearchParams()
	const code = search.get('code')
	const {
		userStore: { changePassword, getPasswordRequest, passwordReset }
	} = useStores()
	const isLoading = useUiStore((s) => s.isLoading)

	const [data, setData] = useState({
		password: '',
		showPassword: false
	})
	const [error, setError] = useState<{
		password: undefined | string
	}>({
		password: undefined
	})
	const [hasBar, setHasBar] = useState<boolean>(false)

	const handleSubmit = async () => {
		const validationError = validatePassword(data.password)
		if (validationError) {
			setError({ ...error, password: validationError })
		} else {
			setError({ password: undefined })
			const isSuccess = await changePassword(data.password, code || '')
			if (isSuccess) {
				navigate('/')
			}
		}
	}

	const handleGetPasswordRequest = async () => {
		if (!code) return setStep('not_found')
		const data = await getPasswordRequest(code)
		if (!data) return setStep('not_found')
		setPasswordRequest(data)
		setStep(data?.expired ? 'link_expired' : 'change_password')
	}

	const handleResendPasswordLink = async () => {
		if (!passwordRequest?.email) return setStep('not_found')
		await passwordReset(passwordRequest.email)
		navigate(`/reset-password?email=${passwordRequest.email}`)
	}
	useEffect(() => {
		const getStep = step === 'change_password'
		setHasBar(getStep)
	}, [step])

	useEffect(() => {
		handleGetPasswordRequest()
	}, [code])

	const validations = {
		length: data.password.length >= 8,
		number: /\d/.test(data.password),
		uppercase: /[A-Z]/.test(data.password)
	}
	const allValid = Object.values(validations).every(Boolean)
	return (
		<div className="bg-t-smoke min-h-screen md:bg-gradient-to-br md:p-8 pt-12 px-4 from-g-purple via-g-cyan to-g-gray">
			<div className="absolute top-6 left-6 md:static md:mb-8">
				<img
					alt="logo"
					className="w-[156px] md:block hidden"
					src="https://storage.googleapis.com/clickguard-storage-dev/public/assets/img/Clickguard%20Logo%20New.svg"
				/>
			</div>

			<NewOnboarding
				backToLogin={step === 'not_found'}
				title={changePasswordSteps[step].title}
				description={changePasswordSteps[step].sub_title}
				hasProgressBar={hasBar}
				progressStepsBar={[90, 0, 0]}
			>
				{step === 'change_password' ? (
					<div className="w-full">
						<div className="relative">
							<GInput
								label="Password"
								className="mb-4"
								value={data.password}
								error={error.password}
								inputClassName={
									' border-t-gray-input leading-4 font-[300] placeholder:text-t-gray-hold'
								}
								onChange={(e) => setData({ ...data, password: e.target.value })}
								type={data.showPassword ? 'text' : 'password'}
								labelClassName="text-t-base-blue leading-4 font-normal mb-1.5"
								errorClassName="text-red-500 leading-4 text-md font-[300] "
							/>
							<div
								className="absolute top-[20px] right-[0px] p-4 cursor-pointer"
								onClick={() =>
									setData({
										...data,
										showPassword: !data.showPassword
									})
								}
							>
								{data.showPassword ? (
									<FiEye width={20} height={20} style={{ color: 'var(--t-gray-hold)' }} />
								) : (
									<HiOutlineEyeOff width={20} height={20} style={{ color: 'var(--t-gray-hold)' }} />
								)}
							</div>
						</div>
						<ul className="space-y-2  text-gray-700">
							<li className="flex items-center text-md font-normal gap-2 text-t-checkbox-unactive">
								{validations.length ? (
									<MdCheckCircle className="text-green-500" size={20} />
								) : (
									<BsCircleFill className="text-t-checkbox" size={16} />
								)}
								8 characters minimum
							</li>
							<li className="flex items-center text-md font-normal gap-2 text-t-checkbox-unactive">
								{validations.number ? (
									<MdCheckCircle className="text-green-500" size={20} />
								) : (
									<BsCircleFill className="text-t-checkbox" size={16} />
								)}
								a number
							</li>
							<li className="flex items-center text-md font-normal gap-2 text-t-checkbox-unactive">
								{validations.uppercase ? (
									<MdCheckCircle className="text-green-500" size={20} />
								) : (
									<BsCircleFill className="text-t-checkbox" size={16} />
								)}
								an uppercase letter
							</li>
						</ul>

						<div className="flex justify-center">
							<GButton
								color="yellow"
								labelClassName="font-base font-medium leading-5"
								className="w-full rounded-full p-3 mt-6 disabled:!shadow-none !shadow-s-yellow"
								iconPlacement="right"
								disabled={
									data.password.length === 0 ||
									isLoading('USER_PASSWORD_CHANGE') ||
									!allValid
								}
								onClick={handleSubmit}
								loader={isLoading('USER_PASSWORD_CHANGE')}
							>
								<span className="px-4">Change password</span>
							</GButton>
						</div>
					</div>
				) : step === 'link_expired' ? (
					<div className="w-full">
						<div className="flex justify-center">
							<GButton
								color="yellow"
								labelClassName="font-base font-medium leading-5"
								className="w-full rounded-full p-3 mt-6 disabled:!shadow-none !shadow-s-yellow"
								iconPlacement="right"
								disabled={isLoading('USER_PASSWORD_RESET')}
								onClick={handleResendPasswordLink}
								loader={isLoading('USER_PASSWORD_RESET')}
							>
								<span className="px-4">Send new reset link</span>
							</GButton>
						</div>
					</div>
				) : (
					<div className="w-full">
						<div className="flex justify-center">
							<GButton
								color="yellow"
								labelClassName="font-base font-medium leading-5"
								className="w-full rounded-full p-3 mt-6 disabled:!shadow-none !shadow-s-yellow"
								iconPlacement="right"
								disabled={isLoading('USER_PASSWORD_RESET')}
								onClick={() => navigate('/reset-password')}
							>
								<span className="px-4">Reset password</span>
							</GButton>
						</div>
					</div>
				)}
			</NewOnboarding>
		</div>
	)
}
